import { useState } from "react";
import { api } from "@/lib/api";
import { Building2, Globe, Eye, Loader2, RefreshCw, ChevronDown, ChevronUp } from "lucide-react";

/**
 * AsymetriesMultiSourcePanel — V23.15.42
 * Triangulation de la position via 3 sources parallèles.
 * Doctrine : projection sur 3 miroirs — annuaire, internet, auto-observation.
 */
export default function AsymetriesMultiSourcePanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/workspace/asymetries/multi-source");
      setData(r.data);
      setExpanded(true);
    } catch {
      setData({ error: true });
    } finally {
      setLoading(false);
    }
  };

  if (!data) {
    return (
      <div className="noam-card p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="asym-multi-source-cta">
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-widest text-[#7F77DD] font-semibold flex items-center gap-1.5">
            <RefreshCw className="w-3 h-3" /> Nouvelle lecture V23.15.42
          </div>
          <div className="text-sm font-semibold text-white mt-1">Analyse multi-source</div>
          <p className="text-xs text-zinc-400 mt-1 leading-relaxed max-w-2xl">
            Croise 3 miroirs pour lire ta position : ton <strong>annuaire interne</strong>, le <strong>web externe</strong> (Tavily/Brave), et l&apos;<strong>auto-observation</strong> de Noam sur ton activité. Les asymétries deviennent alors la mesure de l&apos;écart entre ces 3 lectures.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="btn-primary text-xs"
          data-testid="asym-multi-source-run"
        >
          {loading ? <><Loader2 className="w-3 h-3 animate-spin" /> Analyse…</> : "Lancer l'analyse →"}
        </button>
      </div>
    );
  }

  if (data.error) {
    return (
      <div className="noam-card p-4 text-xs text-red-400" data-testid="asym-multi-source-error">
        Analyse multi-source indisponible. <button onClick={() => setData(null)} className="underline">Réessayer</button>
      </div>
    );
  }

  const { sources, position_narrative, org_secteur, doctrine_reminder } = data;

  return (
    <div className="noam-card p-4 sm:p-5 space-y-4" data-testid="asym-multi-source-result">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-[#7F77DD] font-semibold">
            Analyse multi-source · {org_secteur}
          </div>
          <div className="text-sm font-semibold text-white mt-1">Triangulation de ta position</div>
        </div>
        <button onClick={() => setExpanded(!expanded)} className="btn-ghost text-xs" data-testid="asym-multi-source-toggle">
          {expanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
          {expanded ? "Réduire" : "Détails"}
        </button>
      </div>

      {expanded && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="asym-multi-source-tiles">
            <SourceTile
              icon={Building2}
              color="#1D9E75"
              label="Annuaire interne"
              found={sources?.annuaire?.found || 0}
              narrative={sources?.annuaire?.narrative}
              sample={sources?.annuaire?.peers_sample}
            />
            <SourceTile
              icon={Globe}
              color="#378ADD"
              label="Web externe"
              found={sources?.internet?.found || 0}
              narrative={sources?.internet?.narrative}
              signals={sources?.internet?.signals}
            />
            <SourceTile
              icon={Eye}
              color="#EFD127"
              label="Auto-observation"
              found={sources?.auto_observation?.found || 0}
              narrative={sources?.auto_observation?.narrative}
              counts={sources?.auto_observation?.counts}
            />
          </div>

          <div className="border-t border-white/5 pt-3">
            <div className="text-[10px] uppercase tracking-widest text-[#7F77DD] font-semibold mb-2">
              Synthèse doctrinale
            </div>
            <div className="text-xs text-zinc-200 whitespace-pre-line leading-relaxed" data-testid="asym-multi-source-synthesis">
              {position_narrative}
            </div>
          </div>

          <div className="text-[10px] text-zinc-600 italic border-t border-white/5 pt-2">
            {doctrine_reminder}
          </div>

          <button onClick={load} className="btn-ghost text-xs" data-testid="asym-multi-source-refresh">
            <RefreshCw className="w-3 h-3" /> Rafraîchir
          </button>
        </>
      )}
    </div>
  );
}

function SourceTile({ icon: Icon, color, label, found, narrative, sample, signals, counts }) {
  return (
    <div
      className="rounded-md p-3 border"
      style={{ background: `${color}0A`, borderColor: `${color}40` }}
    >
      <div className="flex items-center gap-2">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <span className="text-[10px] uppercase tracking-widest font-semibold" style={{ color }}>
          {label}
        </span>
        <span className="ml-auto text-lg font-bold tabular-nums" style={{ color }}>{found}</span>
      </div>
      <p className="text-xs text-zinc-300 mt-2 leading-relaxed">{narrative}</p>
      {sample && sample.length > 0 && (
        <div className="mt-2 text-[10px] text-zinc-500 truncate" title={sample.join(", ")}>
          {sample.slice(0, 3).join(" · ")}
        </div>
      )}
      {signals && signals.length > 0 && (
        <ul className="mt-2 space-y-1">
          {signals.slice(0, 3).map((s, i) => (
            <li key={i} className="text-[10px]">
              <a href={s.url} target="_blank" rel="noopener noreferrer" className="text-zinc-400 hover:text-white truncate block">
                · {s.title}
              </a>
            </li>
          ))}
        </ul>
      )}
      {counts && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {Object.entries(counts).filter(([, v]) => v !== null).map(([k, v]) => (
            <span key={k} className="text-[9px] px-1.5 py-0.5 rounded" style={{ background: `${color}18`, color }}>
              {k}: {v}
            </span>
          ))}
        </div>
      )}
    </div>
  );
}
